import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Shield, Zap, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentSubscribe = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('6_months');
  const [processing, setProcessing] = useState(false);

  const plans = [
    {
      id: '3_months',
      months: 3,
      price: 3,
      pricePerMonth: 1,
      savings: null,
      badge: null
    },
    {
      id: '6_months',
      months: 6,
      price: 6,
      pricePerMonth: 1,
      savings: null,
      badge: 'Best Value'
    },
    {
      id: '12_months',
      months: 12,
      price: 12,
      pricePerMonth: 1,
      savings: null,
      badge: 'Most Popular'
    }
  ];

  const handleCheckout = async () => {
    setProcessing(true);
    
    try {
      const plan = plans.find(p => p.id === selectedPlan);
      
      // Create Stripe checkout session
      const response = await axios.post(`${API}/talent/subscribe`, {
        plan_months: plan.months,
        amount: plan.price
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
      
    } catch (error) {
      toast.error('Failed to start checkout');
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 dark:from-gray-900 dark:to-green-950">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Choose Your GoTalent Plan
          </h1>
          <p className="text-xl text-muted-foreground">
            Get discovered by top employers. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`cursor-pointer border-2 rounded-2xl p-8 transition-all ${
                selectedPlan === plan.id
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-xl scale-105'
                  : 'border-border bg-card hover:border-green-300'
              }`}
            >
              {plan.badge && (
                <Badge className="mb-4 bg-green-500 text-white">{plan.badge}</Badge>
              )}
              
              <div className="text-center">
                <div className="text-5xl font-bold text-green-600 mb-2">${plan.price}</div>
                <div className="text-muted-foreground mb-6">for {plan.months} months</div>
                
                <div className="text-sm text-muted-foreground mb-6">
                  ${plan.pricePerMonth} per month
                </div>

                <ul className="space-y-3 text-left">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Profile discoverable to employers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Video introduction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Receive job offers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Interview invitations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">In-app messaging</span>
                  </li>
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4 pb-6 border-b border-border">
                  <Shield className="w-12 h-12 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">Secure Checkout</h3>
                    <p className="text-sm text-muted-foreground">Powered by Stripe. Your payment is safe.</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Selected Plan</span>
                    <span className="font-semibold text-foreground">
                      {plans.find(p => p.id === selectedPlan)?.months} months
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Price</span>
                    <span className="font-semibold text-foreground">
                      ${plans.find(p => p.id === selectedPlan)?.price} AUD
                    </span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-border">
                    <span className="font-semibold text-foreground">Total Due Today</span>
                    <span className="text-2xl font-bold text-green-600">
                      ${plans.find(p => p.id === selectedPlan)?.price}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={processing}
                  className="w-full px-6 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : 'Proceed to Checkout'}
                </button>

                <p className="text-xs text-center text-muted-foreground">
                  By subscribing, you agree to our Terms of Service and Privacy Policy.
                  Cancel anytime from your dashboard.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TalentSubscribe;
